import { isBezorgbaarEmail } from '../email/bezorgbaar.js';
import { isRelayAdres } from './relay-adres.js';

export type EffectieveBronsoort =
  | 'email'
  | 'relay'
  | 'whatsapp_text'
  | 'whatsapp_media'
  | 'whatsapp_reaction';

/** Minimale structurele bronvorm die ook rechtstreeks door de UI kan worden gebruikt. */
export interface InboxBronBerichtInvoer {
  id: string;
  conversation_id?: string | null;
  channel?: string | null;
  is_incoming?: boolean | null;
  from_email?: string | null;
  from_name?: string | null;
  reply_to_email?: string | null;
  reply_to_name?: string | null;
  subject?: string | null;
  body_text?: string | null;
  body_html?: string | null;
  message_date?: string | null;
  has_attachments?: boolean | null;
  gmail_message_id?: string | null;
  external_id?: string | null;
  inbox_effective_source_type?: string | null;
  effective_source_type?: string | null;
}

/** Serverrijen behouden daarnaast hun overige, dynamisch geselecteerde velden. */
export interface InboxBronBericht extends InboxBronBerichtInvoer {
  [key: string]: unknown;
}

export interface InboxBronGesprek {
  channel?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  phone?: string | null;
}

export interface EffectieveKlantbronBasis<TBericht extends InboxBronBerichtInvoer = InboxBronBericht> {
  bericht: TBericht;
  bronsoort: EffectieveBronsoort;
  afzender: string;
  afzenderNaam: string | null;
}

export type ActueleKlantbronBasisResultaat<TBericht extends InboxBronBerichtInvoer = InboxBronBericht> =
  | { status: 'gevonden'; bron: EffectieveKlantbronBasis<TBericht> }
  | { status: 'geen' }
  | { status: 'meervoudig'; bronnen: EffectieveKlantbronBasis<TBericht>[] };

const geldigeKlantmail = (waarde: string | null | undefined): string | null => {
  const email = (waarde || '').trim().toLowerCase();
  return isBezorgbaarEmail(email) && !isRelayAdres(email) ? email : null;
};

const whatsappType = (bericht: InboxBronBerichtInvoer): EffectieveBronsoort => {
  const opgeslagenType = bericht.inbox_effective_source_type || bericht.effective_source_type;
  if (opgeslagenType === 'whatsapp_reaction') return 'whatsapp_reaction';
  if (opgeslagenType === 'whatsapp_media') return 'whatsapp_media';
  if (opgeslagenType === 'whatsapp_text') return 'whatsapp_text';

  const tekst = (bericht.body_text || '').trim().toLowerCase();
  if (/^\[(?:reaction|reactie)\b/.test(tekst)) return 'whatsapp_reaction';
  if (bericht.has_attachments) return 'whatsapp_media';
  return 'whatsapp_text';
};

/** Een echte inbound of een relay met expliciet bezorgbaar Reply-To. */
export function isEffectiefKlantbericht(bericht: InboxBronBerichtInvoer): boolean {
  if (bericht.is_incoming === true) return true;
  if ((bericht.channel || 'email') === 'whatsapp') return false;
  return geldigeKlantmail(bericht.reply_to_email) !== null;
}

export function classificeerBronsoort(bericht: InboxBronBerichtInvoer): EffectieveBronsoort | null {
  if (!isEffectiefKlantbericht(bericht)) return null;
  if ((bericht.channel || 'email') === 'whatsapp') return whatsappType(bericht);
  if (geldigeKlantmail(bericht.reply_to_email) && isRelayAdres(bericht.from_email)) return 'relay';
  return bericht.is_incoming === true ? 'email' : 'relay';
}

export function effectieveAfzender(
  bericht: InboxBronBerichtInvoer,
  gesprek: InboxBronGesprek = {},
): { adres: string; naam: string | null } | null {
  const bronsoort = classificeerBronsoort(bericht);
  if (!bronsoort) return null;

  if (bronsoort.startsWith('whatsapp_')) {
    const telefoon = String(bericht.from_email || gesprek.phone || gesprek.from_email || '').trim();
    return telefoon ? { adres: telefoon, naam: bericht.from_name || gesprek.from_name || null } : null;
  }
  if (bronsoort === 'relay') {
    const replyTo = geldigeKlantmail(bericht.reply_to_email);
    return replyTo ? { adres: replyTo, naam: bericht.reply_to_name || gesprek.from_name || null } : null;
  }

  const replyTo = geldigeKlantmail(bericht.reply_to_email);
  const from = geldigeKlantmail(bericht.from_email);
  const gesprekEmail = geldigeKlantmail(gesprek.from_email);
  const adres = replyTo || from || gesprekEmail;
  if (!adres) return null;
  return {
    adres,
    naam: replyTo
      ? bericht.reply_to_name || bericht.from_name || gesprek.from_name || null
      : bericht.from_name || gesprek.from_name || null,
  };
}

export function maakEffectieveKlantbronBasis<TBericht extends InboxBronBerichtInvoer>(
  bericht: TBericht,
  gesprek: InboxBronGesprek = {},
): EffectieveKlantbronBasis<TBericht> | null {
  const bronsoort = classificeerBronsoort(bericht);
  const afzender = effectieveAfzender(bericht, gesprek);
  if (!bronsoort || !afzender) return null;
  return {
    bericht,
    bronsoort,
    afzender: afzender.adres,
    afzenderNaam: afzender.naam,
  };
}

const datumMs = (bericht: InboxBronBerichtInvoer): number => {
  const waarde = Date.parse(bericht.message_date || '');
  return Number.isFinite(waarde) ? waarde : 0;
};

/**
 * Centrale, browserveilige keuze uit de unie van geldige inbound en relay.
 * Server en store gebruiken deze functie direct. Gelijke nieuwste tijden zijn
 * bewust meervoudig en worden nooit op array- of databasevolgorde beslecht.
 */
export function kiesActueleKlantbronBasis<TBericht extends InboxBronBerichtInvoer>(
  berichten: readonly TBericht[],
  gesprek: InboxBronGesprek = {},
): ActueleKlantbronBasisResultaat<TBericht> {
  const bronnen = berichten
    .map((bericht) => maakEffectieveKlantbronBasis(bericht, gesprek))
    .filter((bron): bron is EffectieveKlantbronBasis<TBericht> => bron !== null)
    .sort((a, b) => datumMs(b.bericht) - datumMs(a.bericht));

  const nieuwste = bronnen[0];
  if (!nieuwste) return { status: 'geen' };
  const zelfdeMoment = bronnen.filter((bron) => datumMs(bron.bericht) === datumMs(nieuwste.bericht));
  if (zelfdeMoment.length > 1) {
    // Zelfde seconde van DEZELFDE afzender is geen twijfelgeval: WhatsApp
    // levert een fotoserie als losse berichten met één tijdstip (Jacqueline
    // 14-09: drie "[image ontvangen]" → 409). Kies dan vast: een bericht met
    // eigen tekst boven een media-placeholder, daarna de hoogste id. Alleen
    // verschillende afzenders op hetzelfde moment blijven 'meervoudig'.
    const afzenders = new Set(zelfdeMoment.map((bron) => String(bron.afzender || '').toLowerCase()));
    const bronsoorten = new Set(zelfdeMoment.map((bron) => bron.bronsoort));
    if (afzenders.size > 1 || bronsoorten.size > 1) return { status: 'meervoudig', bronnen: zelfdeMoment };
    const placeholder = (bron: EffectieveKlantbronBasis<TBericht>) => /^\s*\[[^\]]* ontvangen\]\s*$/i.test(String(bron.bericht.body_text || '').trim());
    const gekozen = [...zelfdeMoment].sort((a, b) => {
      const pa = placeholder(a) ? 1 : 0, pb = placeholder(b) ? 1 : 0;
      if (pa !== pb) return pa - pb;
      return String(b.bericht.id).localeCompare(String(a.bericht.id));
    })[0]!;
    return { status: 'gevonden', bron: gekozen };
  }
  return { status: 'gevonden', bron: nieuwste };
}
